import useToast from '#/common/hooks/useToast';
import { useCallback } from 'react';
import { FileUploadProps } from './FileUploadProps';
import pipe from '#/common/utils/pipe';

const validateFiles = (
  files: File[],
  validate: (file: File) => boolean,
  onError: (invalidFiles: File[]) => void,
): File[] => {
  const valid: File[] = [];
  const invalid: File[] = [];

  files.forEach((file) => {
    if (validate(file)) {
      valid.push(file);
    } else {
      invalid.push(file);
    }
  });

  if (invalid.length > 0) {
    onError(invalid);
  }

  return valid;
};

const filesTxt = (files: File[]): string => (files.length === 1 ? 'file' : 'files');
const fileNames = (files: File[]): string => files.map((file) => file.name).join(', ');

const useFileValidation = (
  options: Pick<FileUploadProps, 'acceptedFormats' | 'maxFileSizeBytes'>,
) => {
  const { acceptedFormats, maxFileSizeBytes } = options;
  const { alert } = useToast();

  const validateFormat = useCallback(
    (files: File[]) => {
      return validateFiles(
        files,
        (file) => {
          if (!acceptedFormats || acceptedFormats === '*') {
            return true;
          }
          const fileExtension = '.' + file.name.toLowerCase().split('.').pop();
          return acceptedFormats.some((format) => format.toLowerCase() === fileExtension);
        },
        (invalidFiles) => {
          if (invalidFiles.length === files.length) {
            alert('error', `Invalid file format. Only ${acceptedFormats} files are accepted.`);
            return;
          }
          alert(
            'error',
            `The following files have invalid formats and will be ignored: ${fileNames(invalidFiles)}. Only ${acceptedFormats} files are accepted.`,
          );
        },
      );
    },
    [acceptedFormats, alert],
  );

  const validateSize = useCallback(
    (files: File[]) => {
      return validateFiles(
        files,
        (file) => {
          if (!maxFileSizeBytes) {
            return true;
          }
          return file.size <= maxFileSizeBytes;
        },
        (oversizedFiles) => {
          if (oversizedFiles.length === files.length) {
            alert(
              'error',
              `Selected ${filesTxt(oversizedFiles)} exceed the maximum allowed size of ${maxFileSizeBytes} bytes.`,
            );
            return;
          }
          alert(
            'error',
            `The following files exceed the maximum allowed size of ${maxFileSizeBytes} bytes and will be ignored: ${fileNames(oversizedFiles)}.`,
          );
        },
      );
    },
    [maxFileSizeBytes, alert],
  );

  return useCallback((files: File[]): File[] => {
    const validate = pipe(validateFormat, validateSize);
    return validate(files);
  }, []);
};

export default useFileValidation;
